/**
 * Iter-408 — Selective Result Publishing UI.
 *
 * Renders the full roster of (student × subject) rows for one exam with:
 *   - checkbox on every row + master select-all
 *   - status pill (published / withheld / unmarked)
 *   - inline metadata for withheld rows (reason, who, when)
 *   - toolbar: Bulk publish selected · Bulk withhold selected · Publish all
 *   - withhold reason dialog before writing
 *
 * Backend:
 *   GET  /school/tenants/{tid}/exams/{eid}/results/roster
 *   POST /school/tenants/{tid}/exams/{eid}/publish-results   {student_ids?, ...}
 *   POST /school/tenants/{tid}/exams/{eid}/withhold-results  {student_ids, reason}
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Send, EyeOff, Loader2, CheckSquare, Filter, Search, Users, AlertCircle,
  MessageCircle, Settings, FileUp,
} from 'lucide-react';
import NotificationTemplatesDialog from './NotificationTemplatesDialog';
import BulkGradeDialog from './BulkGradeDialog';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _token = () => {
  try {
    const pt = sessionStorage.getItem('school_preview_token');
    if (pt) return pt;
  } catch { /* noop */ }
  return getToken();
};

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: {
      Authorization: `Bearer ${_token()}`,
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const STATE_META = {
  published: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Published' },
  withheld:  { color: 'bg-rose-100    text-rose-700    border-rose-200',    label: 'Withheld' },
  draft:     { color: 'bg-slate-100   text-slate-600   border-slate-200',   label: 'Not published' },
  unmarked:  { color: 'bg-amber-100   text-amber-700   border-amber-200',   label: 'Unmarked' },
};

const StateBadge = ({ state }) => {
  const m = STATE_META[state] || STATE_META.draft;
  return <Badge variant="outline" className={m.color}>{m.label}</Badge>;
};

const fmtDT = (v) => v ? new Date(v).toLocaleString() : '—';
const rowKey = (r) => `${r.student_id}__${r.subject_id}`;

// ============= Notification Preview Dialog =============
const NotifyPreviewDialog = ({ open, onOpenChange, exam, tenantId, studentIds, onConfirmPublish }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['publish-preview', exam.id, JSON.stringify(studentIds)],
    queryFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/publish-preview`, {
      method: 'POST',
      body: JSON.stringify({
        notify_parents: true,
        ...(studentIds?.length ? { student_ids: studentIds } : {}),
      }),
    }),
    enabled: open && !!exam.id,
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="notify-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-indigo-500" />
            Notification preview
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : !data ? (
          <div className="text-sm text-slate-500">No preview available.</div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap text-xs">
              <Badge variant="outline"><Users className="h-3 w-3 mr-1" />{data.total_messages || 0} total</Badge>
              <Badge className="bg-emerald-100 text-emerald-700">
                <Send className="h-3 w-3 mr-1" />{data.will_send || 0} will send
              </Badge>
              {data.skipped_no_channel > 0 && (
                <Badge className="bg-amber-100 text-amber-700">
                  {data.skipped_no_channel} no channel
                </Badge>
              )}
            </div>
            <div className="max-h-72 overflow-auto border rounded bg-slate-50">
              {(data.previews || []).map((p, i) => (
                <div key={i} className="border-b p-2 text-sm last:border-0"
                     data-testid={`notify-preview-row-${i}`}>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                    <b className="text-slate-700">{p.parent_name}</b>
                    <span>· {p.student_name}</span>
                    <span className={p.will_send ? 'text-emerald-600' : 'text-amber-600'}>
                      {p.phone || 'no phone'}
                    </span>
                  </div>
                  <p className="text-xs whitespace-pre-wrap">{p.message}</p>
                </div>
              ))}
              {(data.previews || []).length === 0 && (
                <div className="p-4 text-center text-slate-400 text-sm">
                  No messages to preview — no matching students have marks yet.
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {onConfirmPublish && (
            <Button onClick={() => { onConfirmPublish(); onOpenChange(false); }}
                    disabled={!data || (data.will_send || 0) === 0}
                    data-testid="notify-preview-confirm">
              <Send className="h-4 w-4 mr-1" /> Publish &amp; send
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============= Withhold Dialog =============
const WithholdDialog = ({ open, onOpenChange, count, onSubmit }) => {
  const [reason, setReason] = useState('');
  React.useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="withhold-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EyeOff className="h-4 w-4 text-rose-500" /> Withhold {count} result{count === 1 ? '' : 's'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-xs text-slate-500">
            Withheld students will not see this exam under <b>My Grades</b>. They see a
            neutral <i>&quot;Your result is not yet published.&quot;</i> message instead.
          </p>
          <Label className="text-xs">Reason (optional — shown in audit log)</Label>
          <Input
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Pending re-marking of essay section"
            data-testid="withhold-reason"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={() => { onSubmit(reason.trim()); onOpenChange(false); }}
                  data-testid="withhold-confirm">
            <EyeOff className="h-4 w-4 mr-1" /> Withhold
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============= Main Panel =============
const ResultsRosterPanel = ({ exam, tenantId, subjects = [] }) => {
  const [subjectId, setSubjectId] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [askWithhold, setAskWithhold] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const params = new URLSearchParams();
  if (subjectId !== 'all') params.set('subject_id', subjectId);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['exam-roster', exam.id, subjectId],
    queryFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/results/roster?${params.toString()}`),
    enabled: !!exam.id,
  });
  const rows = data?.rows || [];
  const counts = data?.counts || {};

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (stateFilter !== 'all' && r.state !== stateFilter) return false;
      if (!q) return true;
      return (r.student_name || '').toLowerCase().includes(q)
          || (r.admission_number || '').toLowerCase().includes(q);
    });
  }, [rows, search, stateFilter]);

  const selectableIds = useMemo(
    () => filtered.filter(r => r.state !== 'unmarked').map(rowKey),
    [filtered],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every(k => selected.has(k));

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };
  const toggleOne = (k) => setSelected(s => {
    const n = new Set(s);
    n.has(k) ? n.delete(k) : n.add(k);
    return n;
  });

  const selectedStudentIds = useMemo(() => {
    // De-dupe per student — API takes student_ids, not (student, subject) pairs.
    return Array.from(new Set(
      Array.from(selected).map(k => k.split('__')[0]),
    ));
  }, [selected]);

  const publishMutation = useMutation({
    mutationFn: ({ studentIds, all }) => api(
      `/tenants/${tenantId}/exams/${exam.id}/publish-results`,
      {
        method: 'POST',
        body: JSON.stringify({
          notify_parents: true,
          ...(all ? {} : { student_ids: studentIds }),
        }),
      },
    ),
    onSuccess: (r) => {
      toast.success(`${r.published_count} result${r.published_count === 1 ? '' : 's'} published${r.notifications_queued ? ' · parents being notified' : ''}`);
      setSelected(new Set());
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const withholdMutation = useMutation({
    mutationFn: ({ studentIds, reason }) => api(
      `/tenants/${tenantId}/exams/${exam.id}/withhold-results`,
      { method: 'POST', body: JSON.stringify({ student_ids: studentIds, reason }) },
    ),
    onSuccess: (r) => {
      toast.success(`${r.withheld_count} result${r.withheld_count === 1 ? '' : 's'} withheld`);
      setSelected(new Set());
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-3" data-testid="results-roster-panel">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap justify-between">
            <span className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Result publishing roster
            </span>
            <div className="flex items-center gap-2 text-xs">
              <Badge variant="outline">
                <Users className="h-3 w-3 mr-1" /> {counts.total_rows || 0}
              </Badge>
              <Badge className="bg-emerald-100 text-emerald-700">
                {counts.published || 0} published
              </Badge>
              <Badge className="bg-rose-100 text-rose-700">
                {counts.withheld || 0} withheld
              </Badge>
              <Badge className="bg-amber-100 text-amber-700">
                {counts.unmarked || 0} unmarked
              </Badge>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Filters */}
          <div className="flex items-end gap-2 flex-wrap">
            <div className="min-w-0 sm:min-w-[180px] flex-1">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                       className="pl-7 h-8" placeholder="Name or admission #"
                       data-testid="roster-search" />
              </div>
            </div>
            <div className="min-w-0 sm:min-w-[160px]">
              <Label className="text-xs">Subject</Label>
              <Select value={subjectId} onValueChange={setSubjectId}>
                <SelectTrigger className="h-8" data-testid="roster-subject-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subjects</SelectItem>
                  {subjects.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0 sm:min-w-[140px]">
              <Label className="text-xs"><Filter className="h-3 w-3 inline mr-1" />Status</Label>
              <Select value={stateFilter} onValueChange={setStateFilter}>
                <SelectTrigger className="h-8" data-testid="roster-state-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="withheld">Withheld</SelectItem>
                  <SelectItem value="draft">Not published</SelectItem>
                  <SelectItem value="unmarked">Unmarked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bulk toolbar */}
          <div className="flex flex-wrap items-center gap-2 border rounded p-2 bg-slate-50"
               data-testid="roster-bulk-toolbar">
            <span className="text-xs text-slate-600 mr-2">
              <b>{selected.size}</b> selected ({selectedStudentIds.length} student{selectedStudentIds.length === 1 ? '' : 's'})
            </span>
            <Button size="sm"
                    disabled={selected.size === 0 || publishMutation.isPending}
                    onClick={() => publishMutation.mutate({ studentIds: selectedStudentIds })}
                    data-testid="bulk-publish-selected">
              {publishMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                          : <Send className="h-3.5 w-3.5 mr-1" />}
              Publish selected
            </Button>
            <Button size="sm" variant="outline"
                    disabled={publishMutation.isPending}
                    onClick={() => setPreviewOpen(true)}
                    data-testid="preview-notifications-btn">
              <MessageCircle className="h-3.5 w-3.5 mr-1" /> Preview messages
            </Button>
            <Button size="sm" variant="ghost"
                    onClick={() => setTemplatesOpen(true)}
                    data-testid="edit-templates-btn"
                    title="Edit notification templates">
              <Settings className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline"
                    disabled={selected.size === 0 || withholdMutation.isPending}
                    onClick={() => setAskWithhold(true)}
                    data-testid="bulk-withhold-selected">
              <EyeOff className="h-3.5 w-3.5 mr-1" /> Withhold selected
            </Button>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline"
                      onClick={() => setBulkOpen(true)}
                      data-testid="bulk-grade-upload-btn">
                <FileUp className="h-3.5 w-3.5 mr-1" /> Bulk upload
              </Button>
              <Button size="sm" variant="outline"
                      disabled={publishMutation.isPending}
                      onClick={() => {
                        if (window.confirm('Publish ALL marked results for this exam?')) {
                          publishMutation.mutate({ all: true });
                        }
                      }}
                      data-testid="publish-all-explicit">
                <CheckSquare className="h-3.5 w-3.5 mr-1" /> Publish all
              </Button>
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-400" data-testid="roster-empty">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">
                No rows match. Try clearing filters — or enter marks first if the roster is empty.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll}
                              data-testid="roster-select-all" />
                  </TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead className="hidden md:table-cell">Subject</TableHead>
                  <TableHead className="text-right">Mark</TableHead>
                  <TableHead className="text-right">Grade</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden lg:table-cell text-xs">Audit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(r => {
                  const k = rowKey(r);
                  const disabled = r.state === 'unmarked';
                  return (
                    <TableRow key={k} data-testid={`roster-row-${k}`}
                              className={disabled ? 'opacity-60' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={selected.has(k)}
                          onCheckedChange={() => toggleOne(k)}
                          disabled={disabled}
                          data-testid={`roster-select-${k}`}
                        />
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-sm">{r.student_name || r.student_id}</p>
                        {r.admission_number && (
                          <p className="text-xs text-slate-500">{r.admission_number}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-xs hidden md:table-cell">
                        {r.subject_name || r.subject_id || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.marks != null ? (
                          <span className="font-semibold text-sm">
                            {r.marks}<span className="text-xs text-slate-400"> / {r.max_marks ?? '—'}</span>
                          </span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {r.grade || '—'}
                      </TableCell>
                      <TableCell><StateBadge state={r.state} /></TableCell>
                      <TableCell className="text-xs text-slate-500 hidden lg:table-cell">
                        {r.state === 'published' && r.published_at && (
                          <span>Published {fmtDT(r.published_at)}</span>
                        )}
                        {r.state === 'withheld' && (
                          <span>
                            Withheld {fmtDT(r.withheld_at)}
                            {r.withhold_reason && <> · <i>{r.withhold_reason}</i></>}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <WithholdDialog
        open={askWithhold}
        onOpenChange={setAskWithhold}
        count={selectedStudentIds.length}
        onSubmit={(reason) => withholdMutation.mutate({ studentIds: selectedStudentIds, reason })}
      />
      <NotifyPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        exam={exam}
        tenantId={tenantId}
        studentIds={selectedStudentIds}
        onConfirmPublish={() => publishMutation.mutate(
          selectedStudentIds.length ? { studentIds: selectedStudentIds } : { all: true }
        )}
      />
      <NotificationTemplatesDialog
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        tenantId={tenantId}
        focus="grade_published"
      />
      <BulkGradeDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        examId={exam.id}
        subjects={subjects}
        onDone={() => refetch()}
      />
    </div>
  );
};

export default ResultsRosterPanel;
